import React, { useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { useNavigate, useLocation } from "react-router-dom";
import CryptoJS from "crypto-js";
import { CircularProgress } from "@mui/material";
import { useSnackbar } from "notistack";
import { authActions } from "../../../redux-store";
import backend from "../../../api";
import Logo from "../Home/Components/Logo/Logo";
import Classes from "./Auth.module.css";

const genCode = () => Math.floor(1000 + Math.random() * 9000).toString();

function Signup() {
  const [phase, setPhase] = useState("form"); // "form" | "otp"
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [psw, setPsw] = useState("");
  const [psw2, setPsw2] = useState("");
  const [code, setCode] = useState("");
  const [otp, setOtp] = useState(["", "", "", ""]);
  const [loading, setLoad] = useState(false);
  const inputsRef = useRef([]);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { enqueueSnackbar } = useSnackbar();

  // ───────── Validation helpers ─────────
  const emailOK = (v) => /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/i.test(v.trim());
  const phoneOK = (v) => {
    if (!v) return true; // optional
    const digits = v.replace(/\D/g, "");
    return digits.length >= 10;
  };

  const validateForm = () => {
    if (!name.trim()) {
      enqueueSnackbar("Please enter your full name.", { variant: "warning" });
      return false;
    }
    if (!email.trim()) {
      enqueueSnackbar("Please enter your email.", { variant: "warning" });
      return false;
    }
    if (!emailOK(email)) {
      enqueueSnackbar("Enter a valid email address.", { variant: "warning" });
      return false;
    }
    if (!phoneOK(phone)) {
      enqueueSnackbar("Enter a valid phone number (10+ digits).", { variant: "warning" });
      return false;
    }
    if (psw.length < 6) {
      enqueueSnackbar("Password must be at least 6 characters.", { variant: "warning" });
      return false;
    }
    if (psw !== psw2) {
      enqueueSnackbar("Passwords don’t match.", { variant: "warning" });
      return false;
    }
    return true;
  };

  // ───────── Actions ─────────
  const sendCode = async () => {
    if (!validateForm()) return;

    const c = genCode();
    setCode(c);
    setLoad(true);
    try {
      await backend.post("/verify/sendemail", {
        username: name.trim() || "User",
        email: email.trim(),
        message: `verification code is ${c}`,
      });
      sessionStorage.setItem("otpEnc", CryptoJS.AES.encrypt(c, "secret").toString());
      setPhase("otp");
      enqueueSnackbar("OTP sent to your email.", { variant: "info" });
    } catch (e) {
      enqueueSnackbar(e?.response?.data?.message || "Could not send email. Try again.", {
        variant: "error",
      });
    } finally {
      setLoad(false);
    }
  };

  const createAcc = async () => {
    setLoad(true);
    try {
      const ok = await dispatch(
        authActions.signUp(email.trim(), psw, name.trim(), phone.trim())
      );
      if (ok) {
        enqueueSnackbar("Account created successfully!", { variant: "success" });
        const dest = location.state?.data === "toTemplate" ? "/templates" : "/information";
        navigate(dest);
      } else {
        enqueueSnackbar("Account exists or network error.", { variant: "error" });
      }
    } finally {
      setLoad(false);
    }
  };

  const verifyOtp = (e) => {
    e.preventDefault();
    const enc = sessionStorage.getItem("otpEnc");
    if (!enc) {
      enqueueSnackbar("Session expired; please resend OTP.", { variant: "warning" });
      return;
    }
    const real = CryptoJS.AES.decrypt(enc, "secret").toString(CryptoJS.enc.Utf8);
    const entered = otp.join("");

    if (entered.length !== 4) {
      enqueueSnackbar("Enter the 4-digit OTP.", { variant: "warning" });
      return;
    }
    if (entered === real) {
      createAcc();
    } else {
      enqueueSnackbar("Invalid OTP. Please try again.", { variant: "error" });
    }
  };

  const handleOtp = (v, i) => {
    if (!/^\d?$/.test(v)) return;
    const next = [...otp];
    next[i] = v;
    setOtp(next);
    if (v && i < 3) inputsRef.current[i + 1]?.focus();
    if (!v && i > 0) inputsRef.current[i - 1]?.focus();
  };

  return (
    <div className={Classes.page}>
      <div className={Classes.overlay} />
      <div className={Classes.card}>
        {phase === "form" ? (
          <>
            <div className={Classes.logoWrap}>
              <Logo className={Classes.logoIcon} />
            </div>
            <h2 className={Classes.title}>Create Account</h2>
            <p className={Classes.subtitle}>Create an account to get started.</p>

            <label className={Classes.label}>Full Name</label>
            <input
              className={Classes.input}
              placeholder="Enter your full name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
            />

            <label className={Classes.label}>Email</label>
            <input
              className={Classes.input}
              type="email"
              placeholder="Enter your email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />

            <label className={Classes.label}>Phone</label>
            <input
              className={Classes.input}
              placeholder="Enter your phone number"
              value={phone}
              type="number"
              onChange={(e) => setPhone(e.target.value)}
            />

            <label className={Classes.label}>Password</label>
            <input
              className={Classes.input}
              type="password"
              placeholder="Enter your password"
              value={psw}
              onChange={(e) => setPsw(e.target.value)}
              required
            />

            <label className={Classes.label}>Confirm Password</label>
            <input
              className={Classes.input}
              type="password"
              placeholder="Confirm your password"
              value={psw2}
              onChange={(e) => setPsw2(e.target.value)}
              required
            />

            <button
              className={Classes.btn}
              onClick={sendCode}
              disabled={loading}
              style={{ marginTop: "1rem" }}
            >
              {loading ? <CircularProgress size={18} /> : "Sign Up"}
            </button>

            <p className={Classes.alt}>
              Already have an account?{" "}
              <button type="button" className={Classes.link} onClick={() => navigate("/login")}>
                Login
              </button>
            </p>
          </>
        ) : (
          <form onSubmit={verifyOtp} className={Classes.form}>
            <h2 className={Classes.title}>Verify OTP</h2>
            <p className={Classes.subtitle}>Enter the code sent to your email.</p>

            <div className={Classes.otpWrap}>
              {otp.map((d, i) => (
                <input
                  key={i}
                  ref={(el) => (inputsRef.current[i] = el)}
                  className={Classes.otpInput}
                  maxLength={1}
                  value={d}
                  onChange={(e) => handleOtp(e.target.value, i)}
                />
              ))}
            </div>

            <button className={Classes.btn} disabled={loading}>
              {loading ? <CircularProgress size={18} /> : "Submit"}
            </button>

            <p className={Classes.alt}>
              Didn’t get OTP?{" "}
              <button type="button" className={Classes.link} onClick={sendCode}>
                Resend
              </button>
            </p>
          </form>
        )}
      </div>
    </div>
  );
}

export default Signup;
